import { getInitialConsent, isThemeEditor, onConsentChange } from "./consent.js";
import { resolveIdentity, touchSession } from "./storage.js";
import { currentSessionAttribution, firstTouchAttribution } from "./attribution.js";
import { EventBatcher } from "./batch.js";
import { elementRelativePosition, isCustomerTextInput, resolveElementIdentity } from "./elementKey.js";
import { trackScroll } from "./scroll.js";
import { trackSections } from "./sections.js";
import { ensureCartAttribution, getCartToken } from "./cartAttribution.js";
import type { DeviceType, EventType } from "./types.js";
import { observeCartAdds, observeMarketplaceExits } from "./commerce.js";

// Doc section 1: this script must NEVER block rendering, delay Add to
// Cart, delay navigation, or throw into the page. Every public entry point
// below is wrapped so a thrown error here can't propagate into Shopify's
// own JS or a merchant's other apps.

function safe(fn: () => void): void {
  try {
    fn();
  } catch (err) {
    // Deliberately swallow — analytics must fail silently (doc section 1).
    if (window.__HH_DEBUG__) console.warn("[hh-tracker]", err);
  }
}

declare global {
  interface Window {
    __HH_DEBUG__?: boolean;
  }
}

function deviceType(): DeviceType {
  const w = window.innerWidth;
  if (w < 768) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

interface QueuedEvent {
  event_type: EventType;
  client_event_id: string;
  occurred_at: string;
  url_path: string;
  page_type?: string;
  product_id?: string;
  variant_id?: string;
  collection_handle?: string;
  device_type: DeviceType;
  viewport_width: number;
  viewport_height: number;
  document_width: number;
  document_height: number;
  [key: string]: unknown;
}

let seq = 0;
function nextClientEventId(sessionId: string): string {
  seq += 1;
  return `${sessionId}-${seq}-${Date.now()}`;
}

async function init(): Promise<void> {
  const inputContext = window.__HH_CONTEXT__ ?? {};
  // Liquid JSON emits numeric IDs and null/blank on non-product pages.
  // The collector accepts optional strings; one invalid field rejects a batch.
  const optionalText = (value: unknown) => value == null || value === "" ? undefined : String(value);
  const context = {
    ...inputContext,
    product_id: optionalText(inputContext.product_id),
    variant_id: optionalText(inputContext.variant_id),
    product_handle: optionalText(inputContext.product_handle),
    collection_handle: optionalText(inputContext.collection_handle),
    cart_token: optionalText(inputContext.cart_token),
    market: optionalText(inputContext.market),
    currency: optionalText(inputContext.currency),
    page_type: optionalText(inputContext.page_type),
  };
  const endpoint = context.collectEndpoint;
  if (!endpoint) return; // Theme App Extension not configured with a service URL — no-op.

  const themeEditor = isThemeEditor();
  let analyticsAllowed = true;
  if (themeEditor) return;

  const { visitorId, sessionId, sessionStarted } = resolveIdentity();
  const sessionAttribution = currentSessionAttribution();
  firstTouchAttribution(sessionAttribution); // captured once, stored, not re-sent per event (server owns first-touch persistence)

  const device = deviceType();
  let cartToken: string | undefined;
  void getCartToken().then(token => { cartToken = token ?? undefined; }).catch(() => {});

  function buildPayload(events: QueuedEvent[]) {
    return {
      schema_version: 1,
      visitor_id: visitorId,
      session_id: sessionId,
      session_started: sessionStarted,
      consent: { analytics: analyticsAllowed },
      attribution: sessionAttribution,
      market: context.market,
      currency: context.currency,
      cart_token: context.cart_token ?? cartToken,
      is_theme_editor: themeEditor,
      events,
    };
  }

  const batcher = new EventBatcher({
    endpoint,
    buildPayload: (events) => buildPayload(events as QueuedEvent[]),
  });

  function track(eventType: EventType, extra: Partial<QueuedEvent> = {}): void {
    if (!analyticsAllowed || themeEditor) return;
    touchSession();
    const base: QueuedEvent = {
      event_type: eventType,
      client_event_id: nextClientEventId(sessionId),
      occurred_at: new Date().toISOString(),
      url_path: location.pathname,
      page_type: context.page_type ?? (location.pathname.startsWith("/products/") ? "product" : "page"),
      product_id: context.product_id,
      variant_id: context.variant_id,
      collection_handle: context.collection_handle,
      device_type: device,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      document_width: document.documentElement.scrollWidth,
      document_height: document.documentElement.scrollHeight,
      ...extra,
    };
    batcher.push(base);
  }

  onConsentChange((allowed) => {
    const regained = allowed && !analyticsAllowed;
    analyticsAllowed = allowed;
    if (!allowed) batcher.clear();
    if (regained) track("page_view");
  });

  if (!analyticsAllowed || themeEditor) return; // nothing below should even attach listeners

  // --- page_view ---------------------------------------------------------
  track("page_view", {
    metadata: { product_handle: context.product_handle ?? null },
  });
  if (context.product_id || location.pathname.startsWith("/products/")) track("product_view");
  if (location.pathname === "/cart") track("cart_opened");

  // --- clicks (heatmap) ----------------------------------------------------
  const specialClickKeys = new Set(["bundle_add_to_cart", "add_to_cart_button", "variant_option_swatch", "size_finder_link"]);
  let bundleImpressed = false;
  let bundleAccepted = false;
  let bundleIntent = false;

  document.addEventListener(
    "click",
    (event) => {
      safe(() => {
        const target = event.target as Element | null;
        if (!target || !(target instanceof Element)) return;
        if (isCustomerTextInput(target)) return; // never fingerprint clicks inside PII-bearing fields

        const identity = resolveElementIdentity(target);
        const rel = elementRelativePosition(target, event.clientX, event.clientY);
        const docWidth = document.documentElement.scrollWidth;
        const docHeight = document.documentElement.scrollHeight;
        const pageX = event.pageX;
        const pageY = event.pageY;

        track("click", {
          element_key: identity.elementKey,
          element_selector: identity.elementSelector,
          element_relative_x: rel.x,
          element_relative_y: rel.y,
          page_x: pageX,
          page_y: pageY,
          page_x_percent: docWidth ? Math.min(1, pageX / docWidth) : undefined,
          page_y_percent: docHeight ? Math.min(1, pageY / docHeight) : undefined,
          viewport_x_percent: window.innerWidth ? Math.min(1, event.clientX / window.innerWidth) : undefined,
          viewport_y_percent: window.innerHeight ? Math.min(1, event.clientY / window.innerHeight) : undefined,
        });

        if (identity.elementKey === "bundle_add_to_cart") {
          // Intent alone is not successful acceptance.
          bundleIntent = true;
          // Only requests initiated by this click may claim bundle acceptance.
          setTimeout(() => { bundleIntent = false; }, 0);
        }
      });
    },
    { passive: true, capture: true }
  );
  void specialClickKeys;

  // A response observer is the sole AJAX cart-success source. Theme custom
  // events may fire for the same request; listening to both would duplicate it.
  observeCartAdds((type, extra) => {
    track(type, extra);
    if ((extra?.metadata as Record<string, unknown> | undefined)?.bundle === true) {
      bundleAccepted = true;
      bundleIntent = false;
      track("bundle_accepted", { metadata: { source: "confirmed_cart_response" } });
    }
    if (analyticsAllowed) void ensureCartAttribution(visitorId, () => analyticsAllowed);
  }, () => analyticsAllowed && !themeEditor, () => {
    const bundle = bundleIntent;
    bundleIntent = false;
    return { bundle };
  });
  observeMarketplaceExits((type, extra) => { track(type, extra); batcher.flush(true); });

  document.addEventListener("click", event => safe(() => {
    const target = event.target instanceof Element ? event.target : null;
    const size = target?.closest("[data-hh-size-tab]");
    const variant = target?.closest("[data-hh-fit-variant]");
    // Defer until the theme has applied selection state, including dynamic cards.
    queueMicrotask(() => safe(() => {
      if (size?.getAttribute("aria-selected") === "true") track("size_selected", { metadata: { source: "size_finder" } });
      if (variant?.getAttribute("aria-pressed") === "true") track("variant_selected", { variant_id: variant.getAttribute("data-hh-fit-variant") ?? undefined });
    }));
  }), { passive: true });

  // --- variant / size selection --------------------------------------------
  document.addEventListener(
    "change",
    (event) => {
      safe(() => {
        const target = event.target as Element | null;
        if (!target) return;
        const isSize = target.matches('input[data-size-option], select[name*="Size"], select[name*="size"]');
        const isVariantOption = target.matches('input[data-option-value], select[name^="options["]');
        if (isSize) {
          track("size_selected", { metadata: { option_value_id: target.getAttribute("data-option-value-id") } });
        } else if (isVariantOption) {
          track("variant_selected", { metadata: { option_value_id: target.getAttribute("data-option-value-id") } });
        }
      });
    },
    { passive: true, capture: true }
  );

  // --- scroll depth ----------------------------------------------------------
  trackScroll((milestone, maxScrollPercent) => {
    track("scroll_depth", { scroll_depth_percent: milestone, metadata: { max_scroll_percent: Math.round(maxScrollPercent) } });
  });

  // --- section visibility -------------------------------------------------------
  trackSections((sectionKey, timeToFirstViewMs) => {
    track("section_view", { section_key: sectionKey, time_to_first_view_ms: timeToFirstViewMs });
    if (sectionKey === "bundle") {
      bundleImpressed = true;
      track("bundle_impression", { metadata: { component: "hh-bundle-widget" } });
    }
  });

  // --- cart drawer (doc's own interest list) -------------------------------------
  document.addEventListener(
    "click",
    (event) => {
      safe(() => {
        const target = event.target as Element | null;
        if (!target) return;
        if (target.closest("[data-cart-main-checkout], [data-cart-sticky-checkout]")) {
          track("checkout_started", { metadata: { from: "cart_checkout_click" } });
        }
      });
    },
    { passive: true, capture: true }
  );

  // --- bundle_declined: impressed but never accepted, inferred on unload --------
  window.addEventListener("pagehide", () => {
    safe(() => {
      if (bundleImpressed && !bundleAccepted) {
        track("bundle_declined", { metadata: { component: "hh-bundle-widget" } });
      }
      batcher.flush(true);
    });
  });
}

// One bootstrap even if the embed is accidentally included twice. No identity,
// storage or cart reads before consent; a late grant starts capture without reload.
declare global { interface Window { __HH_TRACKER_STARTED__?: boolean } }
safe(() => {
  if (window.__HH_TRACKER_STARTED__) return;
  window.__HH_TRACKER_STARTED__ = true;
  let started = false;
  const start = (allowed: boolean) => {
    if (!allowed || started || isThemeEditor()) return;
    started = true;
    void init().catch(() => {});
  };
  const boot = () => {
    onConsentChange(start);
    void getInitialConsent().then(start).catch(() => {});
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot, { once: true });
  else boot();
});
